import React from 'react';
import Hero from '../components/Hero';
import { useLanguage } from '../contexts/LanguageContext';
import {
  FaMicroscope, FaStar, FaSearch, FaHandshake, FaBalanceScale,
  FaChartBar, FaHospital, FaGlobe, FaUsers, FaHeartbeat,
  FaShieldAlt, FaLightbulb, FaRocket, FaHandsHelping
} from 'react-icons/fa';

const Sobre = () => {
  const { t } = useLanguage();

  const valores = [
    {
      icone: <FaMicroscope className="text-emerald-600" />,
      titulo: t('about.missionVision.values.items.0.title'),
      descricao: t('about.missionVision.values.items.0.description')
    },
    {
      icone: <FaStar className="text-emerald-600" />,
      titulo: t('about.missionVision.values.items.1.title'),
      descricao: t('about.missionVision.values.items.1.description')
    },
    {
      icone: <FaSearch className="text-emerald-600" />,
      titulo: t('about.missionVision.values.items.2.title'),
      descricao: t('about.missionVision.values.items.2.description')
    },
    {
      icone: <FaHandshake className="text-emerald-600" />,
      titulo: t('about.missionVision.values.items.3.title'),
      descricao: t('about.missionVision.values.items.3.description')
    },
    {
      icone: <FaBalanceScale className="text-emerald-600" />,
      titulo: t('about.missionVision.values.items.4.title'),
      descricao: t('about.missionVision.values.items.4.description')
    }
  ];

  const areasAtuacao = [
    {
      icone: <FaChartBar className="text-emerald-600" />,
      titulo: t('about.areas.items.0.title'),
      descricao: t('about.areas.items.0.description')
    },
    {
      icone: <FaHospital className="text-emerald-600" />,
      titulo: t('about.areas.items.1.title'),
      descricao: t('about.areas.items.1.description')
    },
    {
      icone: <FaUsers className="text-emerald-600" />,
      titulo: t('about.areas.items.2.title'),
      descricao: t('about.areas.items.2.description')
    },
    {
      icone: <FaMicroscope className="text-emerald-600" />,
      titulo: t('about.areas.items.3.title'),
      descricao: t('about.areas.items.3.description')
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <Hero
        title={t('about.hero.title')}
        subtitle={t('about.hero.subtitle')}
        description={t('about.hero.description')}
        showButtons={false}
      />

      {/* Quem Somos / História */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-4 mb-12 text-center">
              <div className="inline-flex items-center px-4 py-2 bg-emerald-100 rounded-full">
                <span className="text-emerald-600 text-sm font-medium">// {t('about.whoWeAre.badge').replace('// ', '')}</span>
              </div>

              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight">
                <span className="block">{t('about.whoWeAre.title')}</span>
                <span className="block bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                  {t('about.whoWeAre.titleHighlight')}
                </span>
              </h2>
            </div>

            <div className="space-y-8 text-gray-600 leading-relaxed text-lg text-justify">
              <p>
                {t('about.whoWeAre.paragraphs.0')}
              </p>

              <p>
                {t('about.whoWeAre.paragraphs.1')}
              </p>

              <p>
                {t('about.whoWeAre.paragraphs.2')}
              </p>

              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-l-4 border-emerald-500 p-8 rounded-r-lg text-left">
                <p className="font-medium text-gray-800 text-xl">
                  {t('about.whoWeAre.highlight')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Missão, Visão e Valores */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-emerald-100 rounded-full mb-6">
              <span className="text-emerald-600 text-sm font-medium">// {t('about.missionVision.badge').replace('// ', '')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('about.missionVision.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
            {/* Missão */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100">
              <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('about.missionVision.mission.title')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('about.missionVision.mission.description')}
              </p>
            </div>

            {/* Visão */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100">
              <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">{t('about.missionVision.vision.title')}</h3>
              <p className="text-gray-600 leading-relaxed">
                {t('about.missionVision.vision.description')}
              </p>
            </div>
          </div>

          {/* Valores */}
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 mb-4">{t('about.missionVision.values.title')}</h3>
            <p className="text-xl text-gray-600">
              {t('about.missionVision.values.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-16">
            {valores.map((valor, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-emerald-200">
                <div className="text-4xl mb-4">{valor.icone}</div>
                <h4 className="text-lg font-bold text-gray-900 mb-3">{valor.titulo}</h4>
                <p className="text-gray-600 text-sm leading-relaxed">{valor.descricao}</p>
              </div>
            ))}
          </div>

          {/* Nossa Essência */}
          <div className="bg-gradient-to-br from-emerald-600 to-teal-600 rounded-2xl p-8 text-white shadow-lg max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-3xl font-bold mb-6">{t('about.missionVision.essence.title')}</h3>
              <div className="bg-white/10 rounded-lg p-6">
                <p className="font-medium text-xl mb-4">{t('about.missionVision.essence.quote')}</p>
                <p className="text-emerald-100 text-lg leading-relaxed">
                  {t('about.missionVision.essence.description')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Áreas de Atuação */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-emerald-100 rounded-full mb-6">
              <span className="text-emerald-600 text-sm font-medium">// {t('about.areas.badge').replace('// ', '')}</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('about.areas.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('about.areas.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {areasAtuacao.map((area, index) => (
              <div key={index} className="group bg-gradient-to-br from-gray-50 to-emerald-50 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-emerald-200">
                <div className="flex items-start space-x-4">
                  <div className="text-5xl group-hover:scale-110 transition-transform duration-300">
                    {area.icone}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-emerald-600 transition-colors duration-300">
                      {area.titulo}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {area.descricao}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-teal-900 to-emerald-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-96 h-96 bg-emerald-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-teal-400 rounded-full mix-blend-multiply filter blur-xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            {t('about.cta.title')}
          </h2>
          <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto">
            {t('about.cta.description')}
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a
              href="/trabalhe-conosco"
              className="group bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-8 py-4 rounded-xl font-semibold hover:from-emerald-600 hover:to-teal-700 transition-all duration-300 shadow-2xl hover:shadow-emerald-500/25 flex items-center justify-center space-x-2"
            >
              <span>{t('about.cta.buttons.partner')}</span>
              <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </a>
            <a
              href="/contato"
              className="group border-2 border-white/30 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-slate-900 transition-all duration-300 backdrop-blur-sm flex items-center justify-center space-x-2"
            >
              <span>{t('about.cta.buttons.contact')}</span>
              <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Sobre;